'use strict';

/**
 * azure-functions/shared/executiveAccess.cjs
 *
 * The "Executive dashboard" role permission (msr 4.10c, owner decision D28-A,
 * 2026-10-04; docs/measurement/phase4-plan.md §4.10c). ONE rule, read by:
 *   - measurement-read `view=exec`   who may read the executive index while
 *                                    MSR_PUBLISH_ENABLED is on (the decision)
 *   - src/utils/auth.js              getModulePermissions — what the browser
 *                                    shows (cosmetic; the server decides)
 * Pure — no I/O. The caller hands it the user's STORED role, the Platform
 * Role assigned to them (state.seasuiteRoles, admin-only writable) and the
 * user's stored per-user module permissions (users-crud, admin-only).
 *
 * The permission lives in the existing module-permission system (Settings →
 * Platform Roles, and the user form's module access), under its own key.
 * It deliberately does NOT reuse the `executive` key: every stored role and
 * every saved per-user permission set carries `executive: true` (a new role
 * starts with every module on), and that key never had an effect for a
 * non-admin. Reusing it would grant the view to most non-admins the moment
 * the switch goes on. The new key is absent on every record written before
 * 4.10c, and absent means off.
 *
 *   admin                     always (the base role, never the key)
 *   manager / reader          only an explicit grant:
 *                               - the assigned Platform Role says
 *                                 modules.executiveDashboard === true, or
 *                               - with no Platform Role assigned (or one that
 *                                 no longer exists), the user's own
 *                                 permissions.modules.executiveDashboard
 *                                 .access === true
 *   anything else             never (prospect, portal roles, super-admin)
 */

const EXEC_DASHBOARD_MODULE = 'executiveDashboard';
const GRANTABLE_ROLES = new Set(['manager', 'reader']);

/** The Platform Role assigned to a user, or null (mirrors resolveSeasuiteRole). */
function assignedPlatformRole(user, seasuiteRoles) {
  if (!user || !user.seasuiteRoleId || !Array.isArray(seasuiteRoles)) return null;
  return seasuiteRoles.find((r) => r && r.id === user.seasuiteRoleId) || null;
}

/**
 * @param {{ role?: string, seasuiteRole?: object|null, permissions?: object|null }} input
 * @returns {boolean}
 */
function executiveDashboardAccess({ role, seasuiteRole = null, permissions = null } = {}) {
  if (role === 'admin') return true;
  if (!GRANTABLE_ROLES.has(role)) return false;
  if (seasuiteRole && seasuiteRole.modules && typeof seasuiteRole.modules === 'object') {
    return seasuiteRole.modules[EXEC_DASHBOARD_MODULE] === true;
  }
  const own = permissions && permissions.modules && permissions.modules[EXEC_DASHBOARD_MODULE];
  return Boolean(own) && own.access === true;
}

/** Server side: the decision from the stored user record and the stored roles. */
function storedUserHasExecutiveAccess(user, seasuiteRoles) {
  if (!user) return false;
  return executiveDashboardAccess({
    role: user.role,
    seasuiteRole: assignedPlatformRole(user, seasuiteRoles),
    permissions: user.permissions || null,
  });
}

/**
 * GH #7062: may this user see an ordinary (not grant-only) sidebar module —
 * e.g. `incidents`, `findings`? Mirrors src/utils/auth.js
 * getModulePermissions + Sidebar's `access === false` test: admin always; a
 * manager or reader unless the assigned Platform Role turns the module off,
 * or (no role) their own saved permissions do; anything else never.
 * @param {{ role?: string, seasuiteRole?: object|null, permissions?: object|null }} input
 * @param {string} moduleKey
 * @returns {boolean}
 */
function moduleAccess({ role, seasuiteRole = null, permissions = null } = {}, moduleKey) {
  if (role === 'admin') return true;
  if (!GRANTABLE_ROLES.has(role)) return false;
  if (seasuiteRole && seasuiteRole.modules && typeof seasuiteRole.modules === 'object') {
    return seasuiteRole.modules[moduleKey] !== false;
  }
  const own = permissions && permissions.modules ? permissions.modules[moduleKey] : undefined;
  if (own === undefined || own === null) return true;
  if (typeof own === 'boolean') return own;
  return typeof own === 'object' && own.access !== false;
}

/** Server side: module access from the stored user record and the stored roles. */
function storedUserModuleAccess(user, seasuiteRoles, moduleKey) {
  if (!user) return false;
  return moduleAccess(
    {
      role: user.role,
      seasuiteRole: assignedPlatformRole(user, seasuiteRoles),
      permissions: user.permissions || null,
    },
    moduleKey
  );
}

module.exports = {
  EXEC_DASHBOARD_MODULE,
  assignedPlatformRole,
  executiveDashboardAccess,
  storedUserHasExecutiveAccess,
  moduleAccess,
  storedUserModuleAccess,
};
